import React from 'react';

const TEXTAREA_CONTROLS = new Set(['content', 'subtitle', 'title', 'ctaText', 'ctaLink']);

const TIMELINE_EMPTY_EVENT = {
  date: '',
  title: '',
  description: '',
  image: '',
};

function EditorShell({ title, children }) {
  return (
    <div className="section-editor">
      <div className="editor-header">
        <h3>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
    />
  );
}

function TextAreaInput({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function FileUploadButton({ multiple = false, onChange, label }) {
  return (
    <label className="image-upload-button">
      <input type="file" accept="image/*" multiple={multiple} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function ImagePreview({ src, alt, onRemove }) {
  return (
    <div className="image-preview">
      <img src={src} alt={alt} />
      <button className="remove-image-button" onClick={onRemove} aria-label="Премахни изображение">×</button>
    </div>
  );
}

function ImagePreviewList({ items, renderAlt, onRemove }) {
  if (!items.length) return null;

  return (
    <div className="image-preview-container">
      {items.map((src, index) => (
        <ImagePreview
          key={`${src}-${index}`}
          src={src}
          alt={renderAlt(index)}
          onRemove={() => onRemove(index)}
        />
      ))}
    </div>
  );
}

function SingleImageField({ label, imageUrl, uploadLabel, onUpload, onRemove }) {
  return (
    <FieldGroup label={label}>
      <div className="image-upload-container">
        <FileUploadButton onChange={onUpload} label={uploadLabel} />
        {imageUrl ? (
          <div className="image-preview-container">
            <ImagePreview src={imageUrl} alt="Преглед" onRemove={onRemove} />
          </div>
        ) : null}
      </div>
    </FieldGroup>
  );
}

function MultipleImagesField({ label, images, uploadLabel, onUpload, renderAlt, onRemove }) {
  return (
    <FieldGroup label={label}>
      <div className="image-upload-container">
        <FileUploadButton multiple onChange={onUpload} label={uploadLabel} />
        <ImagePreviewList items={images} renderAlt={renderAlt} onRemove={onRemove} />
      </div>
    </FieldGroup>
  );
}

function createSectionUpdater(sectionId, onUpdateSectionProps) {
  return (patch) => onUpdateSectionProps(sectionId, patch);
}

function EmailFormEditor({ section, onUpdateSectionProps }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);

  return (
    <EditorShell title="Секция с имейл формуляр">
      <FieldGroup label="Имейл адрес на хоста (където ще бъдат изпращани отговорите)">
        <TextInput
          type="email"
          value={section.props.hostEmail}
          onChange={(hostEmail) => update({ hostEmail })}
          placeholder="Въведете имейл адрес"
        />
      </FieldGroup>
      <FieldGroup label="Заглавие на формуляра">
        <TextInput
          value={section.props.title}
          onChange={(title) => update({ title })}
          placeholder="Въведете заглавие на формуляра"
        />
      </FieldGroup>
    </EditorShell>
  );
}

function HeroEditor({ section, onUpdateSectionProps, onImageUpload }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);

  return (
    <EditorShell title="Hero секция">
      <FieldGroup label="Заглавие">
        <TextInput
          value={section.props.title}
          onChange={(title) => update({ title })}
          placeholder="Въведете заглавие"
        />
      </FieldGroup>
      <FieldGroup label="Подзаглавие">
        <TextAreaInput
          value={section.props.subtitle}
          onChange={(subtitle) => update({ subtitle })}
          placeholder="Въведете подзаглавие"
          rows={2}
        />
      </FieldGroup>
      <SingleImageField
        label="Фоново изображение"
        imageUrl={section.props.image}
        uploadLabel="Избери изображение"
        onUpload={(event) => onImageUpload(event, section.id, 'image')}
        onRemove={() => update({ image: '' })}
      />
    </EditorShell>
  );
}

function TextEditor({ section, onUpdateSectionProps }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);

  return (
    <EditorShell title="Текстова секция">
      <FieldGroup label="Съдържание">
        <TextAreaInput
          value={section.props.content}
          onChange={(content) => update({ content })}
          placeholder="Въведете текстовото съдържание"
          rows={4}
        />
      </FieldGroup>
      <FieldGroup label="Подравняване">
        <SelectInput
          value={section.props.alignment || 'center'}
          onChange={(alignment) => update({ alignment })}
          options={[
            { value: 'left', label: 'Ляво' },
            { value: 'center', label: 'Центрирано' },
            { value: 'right', label: 'Дясно' },
          ]}
        />
      </FieldGroup>
    </EditorShell>
  );
}

function BestMenAndMaidsEditor({ section, onUpdateSectionProps, onImageUpload }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);
  const photos = Array.isArray(section.props.photos) ? section.props.photos : [];

  return (
    <EditorShell title="Секция Кумове">
      <FieldGroup label="Заглавие">
        <TextInput
          value={section.props.title}
          onChange={(title) => update({ title })}
          placeholder="Въведете заглавие"
        />
      </FieldGroup>
      <FieldGroup label="Описание">
        <TextAreaInput
          value={section.props.description}
          onChange={(description) => update({ description })}
          placeholder="Въведете описание"
          rows={3}
        />
      </FieldGroup>
      <FieldGroup label="Брой снимки">
        <SelectInput
          value={String(section.props.photoCount || 2)}
          onChange={(photoCount) => update({ photoCount: parseInt(photoCount, 10) })}
          options={[
            { value: '1', label: 'Една снимка' },
            { value: '2', label: 'Две снимки' },
            { value: '3', label: 'Три снимки' },
          ]}
        />
      </FieldGroup>
      <MultipleImagesField
        label="Снимки"
        images={photos}
        uploadLabel="Избери снимки"
        onUpload={(event) => onImageUpload(event, section.id, 'photos')}
        renderAlt={(index) => `Кум/Кума ${index + 1}`}
        onRemove={(index) => update({ photos: photos.filter((_, i) => i !== index) })}
      />
    </EditorShell>
  );
}

function GalleryEditor({ section, onUpdateSectionProps, onImageUpload }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);
  const images = Array.isArray(section.props.images) ? section.props.images : [];

  return (
    <EditorShell title="Галерия секция">
      <MultipleImagesField
        label="Изображения"
        images={images}
        uploadLabel="Избери изображения"
        onUpload={(event) => onImageUpload(event, section.id, 'images')}
        renderAlt={(index) => `Преглед ${index}`}
        onRemove={(index) => update({ images: images.filter((_, i) => i !== index) })}
      />
    </EditorShell>
  );
}

function WeddingPhotoGalleryEditor({ section, onUpdateSectionProps }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);

  return (
    <EditorShell title="Сватбена фотогалерия">
      <FieldGroup label="Заглавие на галерията">
        <TextInput
          value={section.props.title}
          onChange={(title) => update({ title })}
          placeholder="Въведете заглавие на галерията"
        />
      </FieldGroup>
      <FieldGroup label="Описание">
        <TextAreaInput
          value={section.props.description}
          onChange={(description) => update({ description })}
          placeholder="Въведете описание на галерията"
        />
      </FieldGroup>
      <FieldGroup label="Текст на бутона за качване">
        <TextInput
          value={section.props.uploadButtonText || 'Добавете вашата снимка'}
          onChange={(uploadButtonText) => update({ uploadButtonText })}
          placeholder="Въведете текст за бутона за качване"
        />
      </FieldGroup>
      <FieldGroup label="Разреши качване на снимки">
        <input
          type="checkbox"
          checked={section.props.allowUpload !== false}
          onChange={(event) => update({ allowUpload: event.target.checked })}
        />
      </FieldGroup>
    </EditorShell>
  );
}

function CountdownEditor({ section, onUpdateSectionProps }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);

  return (
    <div className="countdown-editor">
      <FieldGroup label="Заглавие">
        <TextInput
          value={section.props.title || 'Отброяване до нашия специален ден'}
          onChange={(title) => update({ title })}
          placeholder="Въведете заглавие за обратно броене"
        />
      </FieldGroup>
      <FieldGroup label="Дата и час">
        <input
          type="datetime-local"
          value={section.props.weddingDate || ''}
          onChange={(event) => update({ weddingDate: event.target.value })}
        />
      </FieldGroup>
      <FieldGroup label="Подравняване">
        <SelectInput
          value={section.props.alignment || 'center'}
          onChange={(alignment) => update({ alignment })}
          options={[
            { value: 'left', label: 'Ляво' },
            { value: 'center', label: 'Центрирано' },
            { value: 'right', label: 'Дясно' },
          ]}
        />
      </FieldGroup>
      <FieldGroup label="Съобщение след изтичане">
        <TextInput
          value={section.props.expiredMessage || 'Денят пристигна!'}
          onChange={(expiredMessage) => update({ expiredMessage })}
          placeholder="Message to show when countdown expires"
        />
      </FieldGroup>
    </div>
  );
}

function cloneTimelineEvents(section) {
  return Array.isArray(section.props.events) ? [...section.props.events] : [];
}

function updateTimelineEvent(section, index, patch, onUpdateSectionProps) {
  const events = cloneTimelineEvents(section);
  events[index] = { ...events[index], ...patch };
  onUpdateSectionProps(section.id, { events });
}

function removeTimelineEvent(section, index, onUpdateSectionProps) {
  const events = cloneTimelineEvents(section).filter((_, itemIndex) => itemIndex !== index);
  onUpdateSectionProps(section.id, { events });
}

function addTimelineEvent(section, onUpdateSectionProps) {
  const events = [...cloneTimelineEvents(section), TIMELINE_EMPTY_EVENT];
  onUpdateSectionProps(section.id, { events });
}

function TimelineEventEditor({ event, index, section, onUpdateSectionProps, onImageUpload }) {
  const handleFieldChange = (fieldName) => (value) => {
    updateTimelineEvent(section, index, { [fieldName]: value }, onUpdateSectionProps);
  };

  return (
    <div className="timeline-event builder-timeline-editor-event">
      <FieldGroup label="Дата">
        <TextInput
          value={event.date}
          onChange={handleFieldChange('date')}
          placeholder="Въведете дата на събитието"
        />
      </FieldGroup>
      <FieldGroup label="Заглавие">
        <TextInput
          value={event.title}
          onChange={handleFieldChange('title')}
          placeholder="Въведете заглавие на събитието"
        />
      </FieldGroup>
      <FieldGroup label="Описание">
        <TextAreaInput
          value={event.description}
          onChange={handleFieldChange('description')}
          placeholder="Въведете описание на събитието"
          rows={3}
        />
      </FieldGroup>
      <SingleImageField
        label="Изображение"
        imageUrl={event.image}
        uploadLabel="Избери изображение"
        onUpload={(eventObj) => onImageUpload(eventObj, section.id, `event-${index}-image`, index)}
        onRemove={() => updateTimelineEvent(section, index, { image: '' }, onUpdateSectionProps)}
      />
      <button
        className="button secondary"
        onClick={() => removeTimelineEvent(section, index, onUpdateSectionProps)}
      >
        Премахни събитие
      </button>
    </div>
  );
}

function TimelineEditor({ section, onUpdateSectionProps, onImageUpload }) {
  const events = Array.isArray(section.props.events) ? section.props.events : [];

  return (
    <div className="timeline-editor">
      <div className="timeline-events">
        {events.map((event, index) => (
          <TimelineEventEditor
            key={`${event.title || 'event'}-${index}`}
            event={event}
            index={index}
            section={section}
            onUpdateSectionProps={onUpdateSectionProps}
            onImageUpload={onImageUpload}
          />
        ))}
      </div>
      <button className="button primary" onClick={() => addTimelineEvent(section, onUpdateSectionProps)}>Добави събитие</button>
    </div>
  );
}

function translateSectionTitle(sectionType) {
  return `${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} Секция`;
}

function renderGenericControlEditor({ control, section, component, onUpdateSectionProps, onImageUpload }) {
  const update = createSectionUpdater(section.id, onUpdateSectionProps);
  const controlValue = section.props[control];
  const defaultValue = component?.defaultProps?.[control] ?? '';

  if (control === 'image') {
    return (
      <SingleImageField
        label="Image"
        imageUrl={controlValue}
        uploadLabel="Избери изображение"
        onUpload={(event) => onImageUpload(event, section.id, control)}
        onRemove={() => update({ [control]: '' })}
      />
    );
  }

  if (control === 'images') {
    const images = Array.isArray(controlValue) ? controlValue : [];

    return (
      <MultipleImagesField
        label="Images"
        images={images}
        uploadLabel="Избери изображения"
        onUpload={(event) => onImageUpload(event, section.id, control)}
        renderAlt={(index) => `Преглед ${index}`}
        onRemove={(index) => update({ [control]: images.filter((_, i) => i !== index) })}
      />
    );
  }

  if (TEXTAREA_CONTROLS.has(control)) {
    return (
      <FieldGroup label={control.charAt(0).toUpperCase() + control.slice(1)}>
        <TextAreaInput
          value={controlValue}
          onChange={(value) => update({ [control]: value })}
          rows={control === 'content' ? 4 : 2}
        />
      </FieldGroup>
    );
  }

  return (
    <FieldGroup label={control.charAt(0).toUpperCase() + control.slice(1)}>
      <GenericControlSelect
        control={control}
        value={controlValue ?? defaultValue}
        onChange={(value) => update({ [control]: value })}
      />
    </FieldGroup>
  );
}

function GenericControlSelect({ control, value, onChange }) {
  const options = getGenericControlOptions(control);
  if (!options.length) {
    return <select value={value} onChange={(event) => onChange(event.target.value)} />;
  }

  return <SelectInput value={value} onChange={onChange} options={options} />;
}

function getGenericControlOptions(control) {
  if (control === 'alignment') {
    return [
      { value: 'left', label: 'Ляво' },
      { value: 'center', label: 'Център' },
      { value: 'right', label: 'Дясно' },
    ];
  }

  if (control === 'captions') {
    return [
      { value: 'true', label: 'Покажи' },
      { value: 'false', label: 'Скрий' },
    ];
  }

  if (control === 'fields') {
    return [
      { value: 'name,email', label: 'Име, Имейл' },
      { value: 'name,email,phone', label: 'Име, Имейл, Телефон' },
      { value: 'name,email,message', label: 'Име, Имейл, Съобщение' },
    ];
  }

  if (control === 'submitText') {
    return [
      { value: 'Submit', label: 'Изпрати' },
      { value: 'Send', label: 'Изпрати' },
      { value: 'Contact Us', label: 'Свържи се с нас' },
    ];
  }

  return [];
}

function GenericControlsEditor({ section, component, onUpdateSectionProps, onImageUpload }) {
  return (
    <EditorShell title={translateSectionTitle(section.type)}>
      {section.type === 'countdown' ? (
        <CountdownEditor section={section} onUpdateSectionProps={onUpdateSectionProps} />
      ) : section.type === 'timeline' ? (
        <TimelineEditor
          section={section}
          onUpdateSectionProps={onUpdateSectionProps}
          onImageUpload={onImageUpload}
        />
      ) : (
        component.controls.map((control) => (
          <React.Fragment key={control}>
            {renderGenericControlEditor({
              control,
              section,
              component,
              onUpdateSectionProps,
              onImageUpload,
            })}
          </React.Fragment>
        ))
      )}
    </EditorShell>
  );
}

function ReadOnlySectionEditor({ section }) {
  return (
    <EditorShell title={translateSectionTitle(section.type)}>
      <p>Тази секция не може да бъде променяна.</p>
    </EditorShell>
  );
}

function buildSectionEditorConfig(sectionType) {
  return {
    emailForm: EmailFormEditor,
    hero: HeroEditor,
    text: TextEditor,
    bestMenAndMaids: BestMenAndMaidsEditor,
    gallery: GalleryEditor,
    weddingPhotoGallery: WeddingPhotoGalleryEditor,
  }[sectionType];
}

function WebsiteBuilderSectionEditor({
  activeSectionId,
  sections,
  componentsMap,
  onUpdateSectionProps,
  onImageUpload,
}) {
  if (!activeSectionId) return null;

  const section = sections.find((item) => item.id === activeSectionId);
  if (!section) return null;

  const component = componentsMap[section.type];
  if (!component) return null;

  const SpecializedEditor = buildSectionEditorConfig(section.type);

  if (SpecializedEditor) {
    return (
      <SpecializedEditor
        section={section}
        onUpdateSectionProps={onUpdateSectionProps}
        onImageUpload={onImageUpload}
      />
    );
  }

  if (component.controls) {
    return (
      <GenericControlsEditor
        section={section}
        component={component}
        onUpdateSectionProps={onUpdateSectionProps}
        onImageUpload={onImageUpload}
      />
    );
  }

  return <ReadOnlySectionEditor section={section} />;
}

export default WebsiteBuilderSectionEditor;
